const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const Cast = require('../../util/cast');

const MAGIC_PEN_ICON = 'data:image/svg+xml;base64,' + Buffer.from(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
  <rect width="40" height="40" rx="8" fill="#6A00FF"/>
  <path d="M10 30 L27 13 L31 17 L14 34 H10 Z" fill="#fff"/>
  <circle cx="28" cy="10" r="3" fill="#FFD700"/>
  <circle cx="33" cy="20" r="2" fill="#00FFFF"/>
  <circle cx="20" cy="7" r="2" fill="#FF4DD8"/>
</svg>
`).toString('base64');

class Scratch3MadBotMagicPen {
    constructor (runtime) {
        this.runtime = runtime;

        this.canvas = null;
        this.ctx = null;
        this.baseCanvas = null;
        this.parent = null;

        this.strokes = [];

        this.offsetX = 0;
        this.offsetY = 0;

        // Used for smooth hand/finger based drawing movement
        this.dragLastX = null;
        this.dragLastY = null;

        this.penState = {
            down: false,
            color: '#00ffff',
            size: 6,

            border: false,
            borderColor: '#ff00ff',
            borderSize: 16,

            glow: false,
            glowColor: '#00ffff',
            glowSize: 18,

            glitter: false,
            glitterAmount: 6,
            glitterSize: 3,
            glitterAnimation: true,

            style: 'normal'
        };

        this.activeTargets = new Map();

        this._ticker = null;
        this._animationFrame = null;

        if (this.runtime && this.runtime.on) {
            this.runtime.on('PROJECT_STOP_ALL', () => {
                this.penUpAll();
                this.stopMovingDrawing();
            });
        }
    }

    getInfo () {
        return {
            id: 'madbotMagicPen',
            name: 'MadBot Magic Pen',
            color1: '#6A00FF',
            color2: '#5000C8',
            color3: '#3A008F',
            menuIconURI: MAGIC_PEN_ICON,
            blockIconURI: MAGIC_PEN_ICON,

            blocks: [
                {
                    blockType: BlockType.LABEL,
                    text: 'Setup'
                },
                {
                    opcode: 'setupMagicPenCanvas',
                    blockType: BlockType.COMMAND,
                    text: 'setup magic pen canvas'
                },
                {
                    opcode: 'clearMagicPen',
                    blockType: BlockType.COMMAND,
                    text: 'clear magic pen'
                },

                {
                    blockType: BlockType.LABEL,
                    text: 'Basic Drawing'
                },
                {
                    opcode: 'penDown',
                    blockType: BlockType.COMMAND,
                    text: 'magic pen down'
                },
                {
                    opcode: 'penUp',
                    blockType: BlockType.COMMAND,
                    text: 'magic pen up'
                },
                {
                    opcode: 'setPenColor',
                    blockType: BlockType.COMMAND,
                    text: 'set pen color [COLOR]',
                    arguments: {
                        COLOR: {
                            type: ArgumentType.COLOR,
                            defaultValue: '#00ffff'
                        }
                    }
                },
                {
                    opcode: 'setPenSize',
                    blockType: BlockType.COMMAND,
                    text: 'set pen size [SIZE]',
                    arguments: {
                        SIZE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 6
                        }
                    }
                },

                {
                    blockType: BlockType.LABEL,
                    text: 'Border / Outline'
                },
                {
                    opcode: 'setBorder',
                    blockType: BlockType.COMMAND,
                    text: 'set border [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOff',
                            defaultValue: 'on'
                        }
                    }
                },
                {
                    opcode: 'setBorderColor',
                    blockType: BlockType.COMMAND,
                    text: 'set border color [COLOR]',
                    arguments: {
                        COLOR: {
                            type: ArgumentType.COLOR,
                            defaultValue: '#ff00ff'
                        }
                    }
                },
                {
                    opcode: 'setBorderSize',
                    blockType: BlockType.COMMAND,
                    text: 'set border size [SIZE]',
                    arguments: {
                        SIZE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 16
                        }
                    }
                },

                {
                    blockType: BlockType.LABEL,
                    text: 'Glow Effect'
                },
                {
                    opcode: 'setGlow',
                    blockType: BlockType.COMMAND,
                    text: 'set glow [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOff',
                            defaultValue: 'on'
                        }
                    }
                },
                {
                    opcode: 'setGlowColor',
                    blockType: BlockType.COMMAND,
                    text: 'set glow color [COLOR]',
                    arguments: {
                        COLOR: {
                            type: ArgumentType.COLOR,
                            defaultValue: '#00ffff'
                        }
                    }
                },
                {
                    opcode: 'setGlowSize',
                    blockType: BlockType.COMMAND,
                    text: 'set glow size [SIZE]',
                    arguments: {
                        SIZE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 18
                        }
                    }
                },

                {
                    blockType: BlockType.LABEL,
                    text: 'Glitter Effect'
                },
                {
                    opcode: 'setGlitter',
                    blockType: BlockType.COMMAND,
                    text: 'set glitter [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOff',
                            defaultValue: 'on'
                        }
                    }
                },
                {
                    opcode: 'setGlitterAmount',
                    blockType: BlockType.COMMAND,
                    text: 'set glitter amount [AMOUNT]',
                    arguments: {
                        AMOUNT: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 6
                        }
                    }
                },
                {
                    opcode: 'setGlitterSize',
                    blockType: BlockType.COMMAND,
                    text: 'set glitter size [SIZE]',
                    arguments: {
                        SIZE: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 3
                        }
                    }
                },
                {
                    opcode: 'setGlitterAnimation',
                    blockType: BlockType.COMMAND,
                    text: 'set glitter animation [STATE]',
                    arguments: {
                        STATE: {
                            type: ArgumentType.STRING,
                            menu: 'onOff',
                            defaultValue: 'on'
                        }
                    }
                },

                {
                    blockType: BlockType.LABEL,
                    text: 'Style Presets'
                },
                {
                    opcode: 'setMagicStyle',
                    blockType: BlockType.COMMAND,
                    text: 'set magic pen style [STYLE]',
                    arguments: {
                        STYLE: {
                            type: ArgumentType.STRING,
                            menu: 'styles',
                            defaultValue: 'neon'
                        }
                    }
                },

                {
                    blockType: BlockType.LABEL,
                    text: 'Manual Drawing'
                },
                {
                    opcode: 'drawLine',
                    blockType: BlockType.COMMAND,
                    text: 'draw magic line x1 [X1] y1 [Y1] x2 [X2] y2 [Y2]',
                    arguments: {
                        X1: {
                            type: ArgumentType.NUMBER,
                            defaultValue: -100
                        },
                        Y1: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        },
                        X2: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 100
                        },
                        Y2: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        }
                    }
                },

                {
                    blockType: BlockType.LABEL,
                    text: 'Move Drawing'
                },
                {
                    opcode: 'moveDrawingWithPoint',
                    blockType: BlockType.COMMAND,
                    text: 'move drawing with x [X] y [Y]',
                    arguments: {
                        X: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        },
                        Y: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        }
                    }
                },
                {
                    opcode: 'stopMovingDrawing',
                    blockType: BlockType.COMMAND,
                    text: 'stop moving drawing'
                },
                {
                    opcode: 'shiftDrawing',
                    blockType: BlockType.COMMAND,
                    text: 'shift drawing x [DX] y [DY]',
                    arguments: {
                        DX: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 20
                        },
                        DY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        }
                    }
                },
                {
                    opcode: 'setDrawingPosition',
                    blockType: BlockType.COMMAND,
                    text: 'set drawing position x [X] y [Y]',
                    arguments: {
                        X: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        },
                        Y: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 0
                        }
                    }
                },
                {
                    opcode: 'resetDrawingPosition',
                    blockType: BlockType.COMMAND,
                    text: 'reset drawing position'
                }
            ],

            menus: {
                onOff: {
                    acceptReporters: true,
                    items: ['on', 'off']
                },
                styles: {
                    acceptReporters: true,
                    items: ['normal', 'outline', 'glow', 'glitter', 'neon', 'rainbow']
                }
            }
        };
    }

    setupMagicPenCanvas () {
        this._createCanvas();
        this._startTicker();
        this._startAnimationLoop();
    }

    clearMagicPen () {
        this._createCanvas();
        this.strokes = [];
        this.dragLastX = null;
        this.dragLastY = null;
        this._clearCanvasOnly();
    }

    penDown (args, util) {
        this._createCanvas();
        this._startTicker();
        this._startAnimationLoop();

        const target = util && util.target;
        if (!target) return;

        const id = this._getTargetId(target);

        this.activeTargets.set(id, {
            target: target,
            lastX: target.x,
            lastY: target.y,
            down: true
        });
    }

    penUp (args, util) {
        const target = util && util.target;
        if (!target) return;

        const id = this._getTargetId(target);
        const data = this.activeTargets.get(id);

        if (data) {
            data.down = false;
        }
    }

    penUpAll () {
        for (const data of this.activeTargets.values()) {
            data.down = false;
        }
    }

    setPenColor (args) {
        this.penState.color = Cast.toString(args.COLOR);
    }

    setPenSize (args) {
        this.penState.size = Math.max(1, Cast.toNumber(args.SIZE));
    }

    setBorder (args) {
        this.penState.border = this._isOn(args.STATE);
    }

    setBorderColor (args) {
        this.penState.borderColor = Cast.toString(args.COLOR);
    }

    setBorderSize (args) {
        this.penState.borderSize = Math.max(1, Cast.toNumber(args.SIZE));
    }

    setGlow (args) {
        this.penState.glow = this._isOn(args.STATE);
    }

    setGlowColor (args) {
        this.penState.glowColor = Cast.toString(args.COLOR);
    }

    setGlowSize (args) {
        this.penState.glowSize = Math.max(0, Cast.toNumber(args.SIZE));
    }

    setGlitter (args) {
        this.penState.glitter = this._isOn(args.STATE);
        this._startAnimationLoop();
    }

    setGlitterAmount (args) {
        this.penState.glitterAmount = Math.max(0, Cast.toNumber(args.AMOUNT));
    }

    setGlitterSize (args) {
        this.penState.glitterSize = Math.max(1, Cast.toNumber(args.SIZE));
    }

    setGlitterAnimation (args) {
        this.penState.glitterAnimation = this._isOn(args.STATE);
        this._startAnimationLoop();
    }

    setMagicStyle (args) {
        const style = Cast.toString(args.STYLE).toLowerCase();
        this.penState.style = style;

        if (style === 'normal') {
            this.penState.border = false;
            this.penState.glow = false;
            this.penState.glitter = false;
        }

        if (style === 'outline') {
            this.penState.border = true;
            this.penState.glow = false;
            this.penState.glitter = false;
        }

        if (style === 'glow') {
            this.penState.border = false;
            this.penState.glow = true;
            this.penState.glitter = false;
        }

        if (style === 'glitter') {
            this.penState.border = true;
            this.penState.glow = true;
            this.penState.glitter = true;
        }

        if (style === 'neon') {
            this.penState.color = '#00ffff';
            this.penState.border = true;
            this.penState.borderColor = '#7b2cff';
            this.penState.borderSize = 18;
            this.penState.glow = true;
            this.penState.glowColor = '#00ffff';
            this.penState.glowSize = 22;
            this.penState.glitter = true;
            this.penState.glitterAmount = 5;
        }

        if (style === 'rainbow') {
            this.penState.border = true;
            this.penState.borderColor = '#ffffff';
            this.penState.borderSize = 14;
            this.penState.glow = true;
            this.penState.glowSize = 18;
            this.penState.glitter = true;
            this.penState.glitterAmount = 4;
        }

        this._startAnimationLoop();
    }

    drawLine (args) {
        this._createCanvas();

        const x1 = Cast.toNumber(args.X1);
        const y1 = Cast.toNumber(args.Y1);
        const x2 = Cast.toNumber(args.X2);
        const y2 = Cast.toNumber(args.Y2);

        this._addStroke(x1, y1, x2, y2);
        this._renderAll();
    }

    moveDrawingWithPoint (args) {
        const x = Cast.toNumber(args.X);
        const y = Cast.toNumber(args.Y);

        if (this.dragLastX === null || this.dragLastY === null) {
            this.dragLastX = x;
            this.dragLastY = y;
            return;
        }

        const dx = x - this.dragLastX;
        const dy = y - this.dragLastY;

        this.offsetX += dx;
        this.offsetY += dy;

        this.dragLastX = x;
        this.dragLastY = y;

        this._renderAll();
    }

    stopMovingDrawing () {
        this.dragLastX = null;
        this.dragLastY = null;
    }

    shiftDrawing (args) {
        this.offsetX += Cast.toNumber(args.DX);
        this.offsetY += Cast.toNumber(args.DY);
        this._renderAll();
    }

    setDrawingPosition (args) {
        this.offsetX = Cast.toNumber(args.X);
        this.offsetY = Cast.toNumber(args.Y);

        this.dragLastX = null;
        this.dragLastY = null;

        this._renderAll();
    }

    resetDrawingPosition () {
        this.offsetX = 0;
        this.offsetY = 0;

        this.dragLastX = null;
        this.dragLastY = null;

        this._renderAll();
    }

    _createCanvas () {
        if (typeof document === 'undefined') return;

        const renderer = this.runtime && this.runtime.renderer;
        const baseCanvas = renderer && renderer.canvas;

        if (!baseCanvas) {
            console.warn('MadBot Magic Pen: Scratch renderer canvas not found.');
            return;
        }

        this.baseCanvas = baseCanvas;
        this.parent = baseCanvas.parentElement;

        if (!this.parent) {
            console.warn('MadBot Magic Pen: Stage parent not found.');
            return;
        }

        if (!this.canvas) {
            this.canvas = document.createElement('canvas');
            this.canvas.setAttribute('data-madbot-magic-pen', 'true');

            this.canvas.style.position = 'absolute';
            this.canvas.style.pointerEvents = 'none';
            this.canvas.style.zIndex = '50';
            this.canvas.style.left = '0px';
            this.canvas.style.top = '0px';

            if (getComputedStyle(this.parent).position === 'static') {
                this.parent.style.position = 'relative';
            }

            this.parent.appendChild(this.canvas);
            this.ctx = this.canvas.getContext('2d');
        }

        this._resizeCanvasIfNeeded();
    }

    _resizeCanvasIfNeeded () {
        if (!this.canvas || !this.baseCanvas || !this.ctx) return;

        const rect = this.baseCanvas.getBoundingClientRect();

        const cssWidth = rect.width || this.baseCanvas.clientWidth || 480;
        const cssHeight = rect.height || this.baseCanvas.clientHeight || 360;

        const dpr = window.devicePixelRatio || 1;

        const realWidth = Math.round(cssWidth * dpr);
        const realHeight = Math.round(cssHeight * dpr);

        this.canvas.style.left = `${this.baseCanvas.offsetLeft}px`;
        this.canvas.style.top = `${this.baseCanvas.offsetTop}px`;
        this.canvas.style.width = `${cssWidth}px`;
        this.canvas.style.height = `${cssHeight}px`;

        if (this.canvas.width !== realWidth || this.canvas.height !== realHeight) {
            this.canvas.width = realWidth;
            this.canvas.height = realHeight;

            this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            this._renderAll();
        }
    }

    _startTicker () {
        if (this._ticker) return;

        this._ticker = setInterval(() => {
            this._tick();
        }, 16);
    }

    _tick () {
        if (!this.canvas) return;

        this._resizeCanvasIfNeeded();

        for (const data of this.activeTargets.values()) {
            if (!data.down || !data.target) continue;

            const target = data.target;
            const currentX = target.x;
            const currentY = target.y;

            if (Math.abs(currentX - data.lastX) > 0.1 || Math.abs(currentY - data.lastY) > 0.1) {
                this._addStroke(data.lastX, data.lastY, currentX, currentY);
                this._renderAll();

                data.lastX = currentX;
                data.lastY = currentY;
            }
        }
    }

    _startAnimationLoop () {
        if (this._animationFrame || typeof requestAnimationFrame === 'undefined') return;

        const loop = () => {
            this._animationFrame = requestAnimationFrame(loop);

            if (this._hasAnimatedGlitter()) {
                this._renderAll();
            }
        };

        this._animationFrame = requestAnimationFrame(loop);
    }

    _hasAnimatedGlitter () {
        if (!this.penState.glitterAnimation) return false;

        return this.strokes.some(stroke => stroke.glitter && stroke.sparkles && stroke.sparkles.length > 0);
    }

    _addStroke (x1, y1, x2, y2) {
    const state = this._copyPenState();

    if (state.style === 'rainbow') {
        const hue = Math.floor((Date.now() / 20) % 360);
        state.color = `hsl(${hue}, 100%, 55%)`;
        state.glowColor = state.color;
    }

    /*
        Important fix:

        The drawing may already be shifted using offsetX / offsetY.
        If we save new strokes directly as x1, y1, x2, y2,
        then new strokes also get shifted again during rendering.

        So we save new strokes by subtracting current offset.
        This makes the new line appear exactly at the finger/sprite position.
    */
    const savedX1 = x1 - this.offsetX;
    const savedY1 = y1 - this.offsetY;
    const savedX2 = x2 - this.offsetX;
    const savedY2 = y2 - this.offsetY;

    const sparkles = state.glitter ?
        this._makeSparkles(savedX1, savedY1, savedX2, savedY2, state) :
        [];

    this.strokes.push({
        x1: savedX1,
        y1: savedY1,
        x2: savedX2,
        y2: savedY2,
        color: state.color,
        size: state.size,

        border: state.border,
        borderColor: state.borderColor,
        borderSize: state.borderSize,

        glow: state.glow,
        glowColor: state.glowColor,
        glowSize: state.glowSize,

        glitter: state.glitter,
        glitterSize: state.glitterSize,
        glitterAnimation: state.glitterAnimation,
        sparkles
    });
}

    _copyPenState () {
        return Object.assign({}, this.penState);
    }

    _makeSparkles (x1, y1, x2, y2, state) {
        const sparkles = [];

        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt((dx * dx) + (dy * dy));

        const amount = Math.max(0, Math.round((distance / 20) * state.glitterAmount));

        const colors = ['#ffffff', '#fff176', '#ff80ff', '#80ffff', '#ffd700'];

        for (let i = 0; i < amount; i++) {
            const t = Math.random();

            const baseX = x1 + (dx * t);
            const baseY = y1 + (dy * t);

            const spread = Math.max(4, state.borderSize / 3);

            sparkles.push({
                x: baseX + ((Math.random() - 0.5) * spread),
                y: baseY + ((Math.random() - 0.5) * spread),
                radius: Math.max(1, state.glitterSize * (0.6 + Math.random())),
                phase: Math.random() * Math.PI * 2,
                color: colors[Math.floor(Math.random() * colors.length)]
            });
        }

        return sparkles;
    }

    _renderAll () {
        if (!this.ctx || !this.canvas) return;

        this._clearCanvasOnly();

        for (const stroke of this.strokes) {
            this._drawStroke(stroke);
        }
    }

    _clearCanvasOnly () {
        if (!this.ctx || !this.canvas) return;

        const width = this.canvas.clientWidth || 480;
        const height = this.canvas.clientHeight || 360;

        this.ctx.clearRect(0, 0, width, height);
    }

    _drawStroke (stroke) {
        if (!this.ctx) return;

        const p1 = this._scratchToCanvas(stroke.x1 + this.offsetX, stroke.y1 + this.offsetY);
        const p2 = this._scratchToCanvas(stroke.x2 + this.offsetX, stroke.y2 + this.offsetY);

        if (stroke.border) {
            this._drawCanvasLine(
                p1.x,
                p1.y,
                p2.x,
                p2.y,
                stroke.borderColor,
                Math.max(stroke.borderSize, stroke.size + 2),
                0,
                stroke.borderColor
            );
        }

        if (stroke.glow) {
            this._drawCanvasLine(
                p1.x,
                p1.y,
                p2.x,
                p2.y,
                stroke.color,
                stroke.size,
                stroke.glowSize,
                stroke.glowColor
            );
        } else {
            this._drawCanvasLine(
                p1.x,
                p1.y,
                p2.x,
                p2.y,
                stroke.color,
                stroke.size,
                0,
                stroke.color
            );
        }

        if (stroke.glitter && stroke.sparkles) {
            this._drawSparkles(stroke);
        }
    }

    _drawCanvasLine (x1, y1, x2, y2, color, size, glowSize, glowColor) {
        const ctx = this.ctx;

        ctx.save();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = color;
        ctx.lineWidth = size;

        if (glowSize > 0) {
            ctx.shadowBlur = glowSize;
            ctx.shadowColor = glowColor;
        }

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.restore();
    }

    _drawSparkles (stroke) {
        const ctx = this.ctx;
        const time = Date.now() / 180;

        for (const sparkle of stroke.sparkles) {
            const p = this._scratchToCanvas(sparkle.x + this.offsetX, sparkle.y + this.offsetY);

            const twinkle = stroke.glitterAnimation ?
                0.45 + (0.55 * Math.abs(Math.sin(time + sparkle.phase))) :
                1;

            ctx.save();
            ctx.globalAlpha = twinkle;
            ctx.fillStyle = sparkle.color;
            ctx.shadowBlur = 8;
            ctx.shadowColor = sparkle.color;

            ctx.beginPath();
            ctx.arc(p.x, p.y, sparkle.radius * twinkle, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        }
    }

    _scratchToCanvas (x, y) {
        const width = this.canvas ? (this.canvas.clientWidth || 480) : 480;
        const height = this.canvas ? (this.canvas.clientHeight || 360) : 360;

        return {
            x: ((x + 240) / 480) * width,
            y: ((180 - y) / 360) * height
        };
    }

    _getTargetId (target) {
        return target.id || (target.sprite && target.sprite.name) || 'stage';
    }

    _isOn (value) {
        const text = Cast.toString(value).toLowerCase();
        return text === 'on' || text === 'true' || text === 'yes' || text === '1';
    }
}

module.exports = Scratch3MadBotMagicPen;